import { db } from '../lib/firebase';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { ContentItem } from './contentService';
import { UserProfile, getUserProfile } from './userService';
import { getPublisherStats } from './subscriptionService';

export interface PublisherStats {
  subscribers: number;
  views: number;
  likes: number;
  shares: number;
  totalContent: number;
}

export interface PublisherContent {
  series: ContentItem[];
  movies: ContentItem[];
  shorts: ContentItem[];
  books: ContentItem[];
  comics: ContentItem[];
  anime: ContentItem[];
  novels: ContentItem[];
}

export interface PublisherProfileData {
  user: UserProfile | null;
  stats: PublisherStats;
  content: PublisherContent;
}

export async function getPublisherProfile(userId: string): Promise<PublisherProfileData> {
  try {
    // Fetch user profile
    const user = await getUserProfile(userId);
    
    // Fetch all content by this user
    const contentQuery = query(
      collection(db, 'content'),
      where('uploaderId', '==', userId),
      where('status', '==', 'published'),
      orderBy('createdAt', 'desc')
    );
    
    const contentSnapshot = await getDocs(contentQuery);
    const allContent: ContentItem[] = contentSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ContentItem));

    // Categorize content
    const content: PublisherContent = {
      series: allContent.filter(item => item.category === 'series'),
      movies: allContent.filter(item => item.category === 'movie'),
      shorts: allContent.filter(item => item.category === 'short'),
      books: allContent.filter(item => item.category === 'book'),
      comics: allContent.filter(item => item.category === 'comic'),
      anime: allContent.filter(item => item.category === 'anime'),
      novels: allContent.filter(item => item.category === 'novel')
    };

    // Get real stats from Firebase
    const stats = await getPublisherStats(userId);

    return {
      user,
      stats,
      content
    };
  } catch (error) {
    console.error('Error fetching publisher profile:', error);
    throw error;
  }
}

export async function getPublisherContentByCategory(userId: string, category: string, limitCount: number = 20): Promise<ContentItem[]> {
  try {
    const contentQuery = query(
      collection(db, 'content'),
      where('uploaderId', '==', userId),
      where('category', '==', category),
      where('status', '==', 'published'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const contentSnapshot = await getDocs(contentQuery);
    return contentSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ContentItem));
  } catch (error) {
    console.error(`Error fetching ${category} content for publisher:`, error);
    return [];
  }
}
